import styled, { css, keyframes } from 'styled-components';

import {
  IButtonIconPosition,
  IButtonSize,
  IButtonVariants,
  IStylesProps,
} from '@components/web/src/atoms/Buttons/types';
import { styleMatcher } from '@components/web/src/foundations';
import { COLORS, RETAILER_COLORS, SHADOWS } from '@components/web/src/styles';

const createPulse = (color: string) => keyframes`
  0% {
    box-shadow: 0 0 0 0 ${color};
  }
  70% {
    box-shadow: 0 0 0 10px color-mix(in srgb, ${color}, transparent 100%);;
  }
  100% {
    box-shadow: 0 0 0 50px color-mix(in srgb, ${color}, transparent 100%);
  }
`;

const getPulseStyles = () => css`
  box-shadow: none;
  border-width: 0;
  animation: ${createPulse(RETAILER_COLORS.primary['600'])} 2s infinite;
`;

export const getVariantStyles = (variant: IButtonVariants = 'primary') =>
  styleMatcher(variant, {
    light: () => css`
      color: ${RETAILER_COLORS.primary['700']};
      border-width: 1px;
      border-color: ${RETAILER_COLORS.primary['300']};
      background-color: ${COLORS.base.white};
      box-shadow: 2px -4px 0px 0px rgba(0, 0, 0, 0.25) inset;

      &:focus {
        box-shadow:
          0 0 0 4px #f2f4f7,
          0 1px 2px 0 rgba(16, 24, 40, 0.05);
      }

      &:disabled {
        border-width: 1px;
        border-color: ${RETAILER_COLORS.primary['300']};
        color: ${RETAILER_COLORS.primary['300']};
        box-shadow: ${SHADOWS.xs};
        background-color: ${COLORS.base.white};

        &:hover {
          background-color: ${RETAILER_COLORS.primary['50']};
        }
      }

      &:hover {
        background-color: ${RETAILER_COLORS.primary['50']};
      }
    `,
    link: () => css`
      color: ${RETAILER_COLORS.primary['700']} !important;
      background-color: transparent;
      border: none;

      &:hover {
        background-color: ${RETAILER_COLORS.primary['50']};
      }

      &:disabled {
        color: ${COLORS.gray[300]} !important;
        background-color: transparent;
        border: none;
        box-shadow: none;
      }
    `,
    primary: () => css`
      background-color: ${RETAILER_COLORS.primary['600']};
      color: ${COLORS.base.white};
      box-shadow: 2px -4px 0px 0px rgba(0, 0, 0, 0.25) inset;
      border-width: 1px;
      border-color: ${RETAILER_COLORS.primary['600']};

      &:focus {
        box-shadow: none;
        border-color: ${RETAILER_COLORS.primary['100']};
        outline: none !important;
      }

      &:hover {
        background-color: ${RETAILER_COLORS.primary['700']};
        box-shadow: 2px -4px 0px 0px rgba(0, 0, 0, 0.25) inset;
      }

      &:disabled {
        background-color: ${RETAILER_COLORS.primary['200']};
        color: ${COLORS.base.white};
        border-color: ${RETAILER_COLORS.primary['200']};
        box-shadow: 2px -4px 0px 0px rgba(0, 0, 0, 0.25) inset;
      }
    `,
    secondary: () => css`
      color: ${RETAILER_COLORS.secondary['900']};
      background-color: ${RETAILER_COLORS.secondary['500']};
      border: none;
      box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);

      &:hover {
        color: ${RETAILER_COLORS.secondary['900']};
        background-color: ${RETAILER_COLORS.secondary['600']};
      }

      &:focus {
        box-shadow:
          0 0 0 4px #f2f4f7,
          0 1px 2px 0 rgba(16, 24, 40, 0.05);
      }

      &:disabled {
        color: ${RETAILER_COLORS.secondary['700']};
        background-color: ${RETAILER_COLORS.secondary['500']};
        box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
      }
    `,
    transparent: () => css`
      color: ${RETAILER_COLORS.primary['700']};
      background: transparent;
      border: none;
    `,
  });

export const getSizeStyles = (size: IButtonSize = 'sm', hasText = true) =>
  styleMatcher(size, {
    lg: () => css`
      padding: 16px 28px;

      svg {
        width: 24px !important;
        height: 24px !important;
      }

      ${!hasText &&
      css`
        padding: 16px 18px;
      `}
    `,

    md: () => css`
      padding: 14px 18px;

      ${!hasText &&
      css`
        padding: 14px 12px;
      `}
    `,

    sm: () => css`
      padding: 10px 14px;

      ${!hasText &&
      css`
        min-width: 38px;
        max-width: 38px;
        padding: 10px 8px;
      `}
    `,
  });

export const Button = styled.button<IStylesProps>`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: ${({ $isFullWidth }) => ($isFullWidth ? '100%' : 'auto')};
  appearance: none;
  border-radius: 8px;
  transition: 0.3s ease-out;
  border-width: 0;
  border-color: transparent;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:disabled,
  &[disabled]:hover,
  &:focus,
  &:hover {
    text-decoration: none !important;
  }

  ${({ $variant }) => getVariantStyles($variant)}
  ${({ $size, $hasText }) => getSizeStyles($size, $hasText)}
  ${({ $variant, $isPulseAnimation }) => $isPulseAnimation && $variant === 'primary' && getPulseStyles()}
`;

export const Icon = styled.span<{ $iconPosition?: IButtonIconPosition }>`
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 20px;
    height: 20px;
  }

  ${({ $iconPosition }) => ($iconPosition === 'right' ? `order: 1` : `order: 0`)}
`;
